<script setup lang="ts">
import { computed, watch } from 'vue'
import { Form, Field } from 'vee-validate'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import WidgetModalShell from '@/components/Dashboard/Modals/WidgetModalShell.vue'
import ButtonLoader from '@/components/Globals/Feedbacks/ButtonLoader.vue'
import type { ISensor } from '@/interfaces/entities/sensor'
import type { Aggregation, IWidget, WidgetSourceType } from '@/interfaces/components/dashboard/widget'
import type { IWidgetFormData } from '@/interfaces/components/dashboard/widget'
import { indicatorWidgetSchema } from '@/schemas/WidgetModalSchema'
import DropdownSensors from '@/components/Globals/Inputs/DropdownSensors.vue'
import WidgetModalDataFieldSection from '@/components/Dashboard/Modals/WidgetModalDataFieldSection.vue'
import { useWidgetModalSensorDetails } from '@/composables/useWidgetModalSensorDetails'
import { useGroupStore } from '@/store/group'
import {
  GROUP_INDICATOR_FIELDS,
  getWidgetGroupId,
  getWidgetSensorId,
  getWidgetSourceType,
} from '@/utils/dashboard/widget-source'
import { useI18n } from '@/composable/useI18n'

const AGGREGATION_VALUES: Aggregation[] = ['mean', 'median', 'last', 'sum', 'max', 'min']

const props = withDefaults(
  defineProps<{
    sensors: ISensor[]
    initialData?: IWidget
    loading?: boolean
  }>(),
  { initialData: undefined, loading: false },
)

const emit = defineEmits<{
  save: [formData: IWidgetFormData]
  cancel: []
}>()

const { t } = useI18n()
const groupStore = useGroupStore()
const { listGroups } = storeToRefs(groupStore)

const {
  selectedSensor,
  sensorDetails,
  isLoadingDetails,
  detailsError,
  unsupportedSensorType,
  onSensorSelected,
  syncSelectedSensorFromEdit,
} = useWidgetModalSensorDetails()

const fieldLabelClass = 'mb-1.5 block text-xs font-bold uppercase tracking-wider text-body'
const fieldInputClass =
  'w-full border border-stroke bg-gray-2 px-3 py-2.5 text-sm text-black outline-none focus:border-primary'
const fieldErrorClass = 'mt-1 text-sm text-red'

const modalTitle = computed(() =>
  props.initialData
    ? t('custom_dashboards.widget_modal.indicator.title_edit')
    : t('custom_dashboards.widget_modal.indicator.title_create'),
)

const selectedDataField = computed(() => props.initialData?.config.data_field ?? '')

const groupFieldOptions = computed(() =>
  GROUP_INDICATOR_FIELDS.map((value) => ({
    value,
    label: t(`custom_dashboards.widget_modal.group_fields.${value}`),
  })),
)

const initialValues = computed(() => {
  if (props.initialData) {
    const sourceType = getWidgetSourceType(props.initialData)
    return {
      title: props.initialData.title,
      sourceType,
      sensorId: getWidgetSensorId(props.initialData) || '',
      groupId: getWidgetGroupId(props.initialData) || '',
      dataField: props.initialData.config.data_field || '',
      aggregation: props.initialData.config.aggregation || 'mean',
    }
  }

  return {
    title: t('custom_dashboards.widget_modal.indicator.default_title'),
    sourceType: 'sensor' as WidgetSourceType,
    sensorId: '',
    groupId: '',
    dataField: '',
    aggregation: 'mean' as Aggregation,
  }
})

watch(
  () => [props.initialData, props.sensors] as const,
  () => {
    void groupStore.fetch()

    if (!props.initialData) {
      void syncSelectedSensorFromEdit(null)
      return
    }

    const sourceType = getWidgetSourceType(props.initialData)
    if (sourceType === 'group') {
      void syncSelectedSensorFromEdit(null)
      return
    }

    const sensorId = getWidgetSensorId(props.initialData) || ''
    const sensor = props.sensors.find((s) => String(s.id) === sensorId) || null
    void syncSelectedSensorFromEdit(sensor)
  },
  { immediate: true },
)

const aggregationOptions = computed(() =>
  AGGREGATION_VALUES.map((value) => ({
    value,
    label: t(`custom_dashboards.widget_modal.indicator.aggregations.${value}`),
  })),
)

function aggregationDescription(aggregation: Aggregation): string {
  return t(`custom_dashboards.widget_modal.indicator.aggregation_descriptions.${aggregation}`)
}

function onSourceTypeChange(
  sourceType: WidgetSourceType,
  setFieldValue: (field: string, value: unknown) => void,
) {
  setFieldValue('sourceType', sourceType)
  setFieldValue('dataField', '')
  setFieldValue('sensorId', '')
  setFieldValue('groupId', '')
  void syncSelectedSensorFromEdit(null)
  if (sourceType === 'group') {
    setFieldValue('aggregation', 'last')
  }
}

function onSubmit(values: {
  title: string
  sourceType: WidgetSourceType
  sensorId?: string
  groupId?: string
  dataField: string
  aggregation: Aggregation
}) {
  emit('save', {
    title: values.title,
    sourceType: values.sourceType,
    sensorId: values.sourceType === 'sensor' ? values.sensorId : undefined,
    groupId: values.sourceType === 'group' ? values.groupId : undefined,
    dataField: values.dataField,
    aggregation: values.sourceType === 'group' ? 'last' : values.aggregation,
  })
}
</script>

<template>
  <WidgetModalShell :loading="loading" @cancel="emit('cancel')">
    <template #title>
      <h2 class="font-satoshi mb-6 text-xl font-bold text-black">
        {{ modalTitle }}
      </h2>
    </template>

    <Form
          :validation-schema="indicatorWidgetSchema"
          :initial-values="initialValues"
          @submit="onSubmit"
          v-slot="{ values, setFieldValue, errors }"
        >
          <div class="space-y-5">
            <Field name="title" v-slot="{ field, errorMessage }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.widget_modal.indicator.fields.title') }}
                </label>
                <input
                  v-bind="field"
                  type="text"
                  :class="fieldInputClass"
                  :placeholder="t('custom_dashboards.widget_modal.indicator.placeholders.title')"
                />
                <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
              </div>
            </Field>

            <Field name="sourceType" v-slot="{ field }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.widget_modal.common.fields.source_type') }}
                </label>
                <div class="flex gap-4">
                  <label class="flex cursor-pointer items-center gap-2 text-sm text-black">
                    <input
                      type="radio"
                      :checked="field.value === 'sensor'"
                      class="border-stroke text-blue focus:ring-blue"
                      @change="onSourceTypeChange('sensor', setFieldValue)"
                    />
                    {{ t('custom_dashboards.widget_modal.common.source_types.sensor') }}
                  </label>
                  <label class="flex cursor-pointer items-center gap-2 text-sm text-black">
                    <input
                      type="radio"
                      :checked="field.value === 'group'"
                      class="border-stroke text-blue focus:ring-blue"
                      @change="onSourceTypeChange('group', setFieldValue)"
                    />
                    {{ t('custom_dashboards.widget_modal.common.source_types.group') }}
                  </label>
                </div>
              </div>
            </Field>

            <div v-if="values.sourceType !== 'group'">
              <label :class="fieldLabelClass">
                {{ t('custom_dashboards.widget_modal.indicator.fields.sensor') }}
              </label>
              <DropdownSensors
                name="sensorId"
                :options="sensors"
                :already-selected-option="selectedSensor || undefined"
                :constrain-to-parent="true"
                @option-selected="(sensor: ISensor) => onSensorSelected(sensor, setFieldValue)"
              />
              <p v-if="errors.sensorId" :class="fieldErrorClass">{{ errors.sensorId }}</p>
            </div>

            <Field v-else name="groupId" v-slot="{ field, errorMessage }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.widget_modal.common.fields.group') }}
                </label>
                <div class="relative">
                  <select
                    v-bind="field"
                    :class="[fieldInputClass, 'appearance-none pr-10']"
                  >
                    <option value="">
                      {{ t('custom_dashboards.widget_modal.common.placeholders.group') }}
                    </option>
                    <option
                      v-for="group in listGroups"
                      :key="group.id"
                      :value="String(group.id)"
                    >
                      {{ group.name }}
                    </option>
                  </select>
                  <ChevronDownIcon
                    class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  />
                </div>
                <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
              </div>
            </Field>

            <WidgetModalDataFieldSection
              v-if="values.sourceType !== 'group'"
              :has-selected-sensor="!!selectedSensor"
              :sensor-details="sensorDetails"
              :is-loading-details="isLoadingDetails"
              :details-error="detailsError"
              :unsupported-sensor-type="unsupportedSensorType"
              :already-selected-data-field="selectedDataField || undefined"
              :field-label-class="fieldLabelClass"
              :field-input-class="fieldInputClass"
              :field-error-class="fieldErrorClass"
            />

            <Field v-else name="dataField" v-slot="{ field, errorMessage }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.widget_modal.common.fields.group_metric') }}
                </label>
                <div class="relative">
                  <select
                    v-bind="field"
                    :class="[fieldInputClass, 'appearance-none pr-10']"
                  >
                    <option value="">
                      {{ t('custom_dashboards.widget_modal.common.placeholders.group_metric') }}
                    </option>
                    <option
                      v-for="option in groupFieldOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                  <ChevronDownIcon
                    class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  />
                </div>
                <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
              </div>
            </Field>

            <Field
              v-if="values.sourceType !== 'group'"
              name="aggregation"
              v-slot="{ field, errorMessage }"
            >
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.widget_modal.indicator.fields.aggregation') }}
                </label>
                <div class="relative">
                  <select
                    v-bind="field"
                    :class="[fieldInputClass, 'appearance-none pr-10 uppercase']"
                  >
                    <option
                      v-for="option in aggregationOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                  <ChevronDownIcon
                    class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  />
                </div>
                <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
              </div>
            </Field>

            <div
              v-if="values.sourceType !== 'group' && values.aggregation"
              class="border-l-4 border-blue bg-blue-50 px-4 py-3"
            >
              <p class="text-sm leading-relaxed text-body">
                {{ aggregationDescription(values.aggregation as Aggregation) }}
              </p>
            </div>

            <div
              v-else-if="values.sourceType === 'group'"
              class="border-l-4 border-blue bg-blue-50 px-4 py-3"
            >
              <p class="text-sm leading-relaxed text-body">
                {{ t('custom_dashboards.widget_modal.common.group_metric_info') }}
              </p>
            </div>
          </div>

          <div class="mt-6 border-t border-stroke pt-6">
            <div class="flex justify-end gap-4">
              <button
                type="button"
                class="text-xs font-medium uppercase tracking-wide text-body hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="loading"
                @click="emit('cancel')"
              >
                {{ t('custom_dashboards.widget_modal.indicator.actions.cancel') }}
              </button>
              <button
                type="submit"
                class="flex h-10 min-w-[7rem] items-center justify-center bg-blue px-6 py-2.5 text-xs font-medium uppercase tracking-wide text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="loading"
              >
                <ButtonLoader v-if="loading" />
                <span v-else>
                  {{
                    initialData
                      ? t('custom_dashboards.widget_modal.indicator.actions.save')
                      : t('custom_dashboards.widget_modal.indicator.actions.create')
                  }}
                </span>
              </button>
            </div>
          </div>
        </Form>
  </WidgetModalShell>
</template>

<style scoped>
.font-satoshi {
  font-family: 'Satoshi', sans-serif;
}
</style>
